import { Button, cn } from '@atlas/ui-primitives'
import type { ReactNode } from 'react'

/**
 * Props for the {@link AddAffordance} component.
 *
 * @property label - What gets added, as a verb phrase: "Add classification"
 * @property hint - Optional second line naming what may be added
 * @property icon - Replaces the leading plus, for an affordance that is not a create
 * @property size - 'sm' for an inline control, 'md' (default) for a block one
 * @property block - Stretches to the container width and centres the label
 * @property onPress - Invoked on press
 * @property aria-label - Overrides the accessible name where the label alone is ambiguous
 * @property aria-expanded - Set when the affordance discloses a picker rather than adding at once
 * @property className - Optional additional Tailwind classes
 */
export interface AddAffordanceProps {
	label: ReactNode
	hint?: ReactNode
	icon?: ReactNode
	size?: 'sm' | 'md'
	block?: boolean
	onPress: () => void
	'aria-label'?: string
	'aria-expanded'?: boolean
	className?: string
}

/**
 * The dashed placeholder that invites adding one more of something, rendered where
 * the new item will appear.
 *
 * @remarks
 * - **Why it exists:** the dashed outline, the muted label and the hover that firms
 *   both up were hand-rolled at five call sites, which had already drifted into three
 *   different hover treatments and two different plus characters. The treatment is the
 *   `dashed` Button variant; this composes it with the label/hint pairing those sites
 *   share.
 * - **Not an empty state:** {@link EmptyState} explains that a collection has nothing
 *   in it. This sits alongside items that already exist and only offers the next one.
 *
 * @example
 * ```tsx
 * <AddAffordance block label="Add variable" hint="dimension or attribute" onPress={open} />
 * ```
 *
 * @see packages/ui-components/src/add-affordance/add-affordance.stories.tsx for visual examples
 */
export function AddAffordance({
	label,
	hint,
	icon,
	size = 'md',
	block = false,
	onPress,
	className,
	...aria
}: AddAffordanceProps) {
	return (
		<Button
			{...aria}
			variant="dashed"
			size="sm"
			onPress={onPress}
			className={cn(
				'h-auto gap-1',
				size === 'sm' ? 'px-2 py-1 text-ds-xs' : 'px-3 py-2 text-ds-sm',
				block && 'w-full flex-col justify-center text-center',
				className,
			)}
		>
			<span className="flex items-center gap-1">
				{icon ?? <span aria-hidden="true">+</span>}
				{label}
			</span>
			{/* Decorative: the label alone is the accessible name, so a hint naming what
			    may be added does not read as part of the action. */}
			{hint ? (
				<span aria-hidden="true" className="text-ds-xs text-ds-text-subtle">
					{hint}
				</span>
			) : null}
		</Button>
	)
}
